import { FC } from 'react';
import { stanAnalytics } from '@/lib';
import { useUser } from '@/queries/useUser';
import { useAuthStore } from '@/stores';
import { useStanRouter } from '@/hooks/useStanRouter';
import IcLogout from '@/assets/icons/ic-log-out.svg';
import IcIntegrations from '@/assets/icons/ic-integrations.svg';
import IcHelpCircle from '@/assets/icons/ic-help-circle.svg';
import DefaultUserImage from '@/assets/images/default-user.svg';

interface AccountsSectionProps {
    onClose: () => void;
}

export const AccountsSection: FC<AccountsSectionProps> = ({ onClose }) => {
    const { user } = useUser();
    const { push } = useStanRouter();
    const logout = useAuthStore(state => state.logout);
    const switchAccount = useAuthStore(state => state.switchAccount);
    const prepareToAddAccount = useAuthStore(state => state.prepareToAddAccount);
    const accounts = useAuthStore(state => state.accounts);
    const currentAccountKey = useAuthStore(state => state.currentAccountKey);

    const handleSwitchAccount = (accountKey: string) => {
        const accountToSwitchTo = accounts[accountKey];
        stanAnalytics('accounts-switch', {
            meta: { user_id: user?.user_id, username: user?.username },
            props: {
                total_accounts: Object.keys(accounts).length,
                user_ids: Object.keys(accounts).map(key => accounts[key]?.user_id),
                switch_to_username: accountToSwitchTo?.username,
            },
        });

        switchAccount(accountKey);
        onClose();
    };

    const handleAddAccount = () => {
        stanAnalytics('accounts-add-click', {
            meta: { user_id: user?.user_id, username: user?.username },
            props: {
                total_accounts: Object.keys(accounts).length,
                user_ids: Object.keys(accounts).map(key => accounts[key]?.user_id),
            },
        });

        prepareToAddAccount();
        push('/login');
        onClose();
    };

    const handleLogout = () => {
        logout();
        push('/login');
        onClose();
    };

    const getProfileImage = (imageUrl?: string) => imageUrl || DefaultUserImage;
    const otherAccounts = Object.entries(accounts).filter(([key]) => key !== currentAccountKey);
    const logoutText = Object.keys(accounts).length > 1 ? 'Logout From All Accounts' : 'Log Out';

    return (
        <div>
            <h3 className='text-dark mb-4'>Accounts</h3>
            <div className='space-y-3'>
                {/* Current Account */}
                {user && (
                    <div className='bg-brand-subtle shadow-nucleus-elevation-medium flex w-full items-center gap-4 rounded-lg p-3'>
                        <img
                            src={getProfileImage(user?.data?.profile_image || user?.profile_image)}
                            alt={user.username}
                            className='border-dark size-9 rounded-full border-2 object-cover'
                        />
                        <div className='min-w-0 flex-1'>
                            <p className='text-label-sm-bold text-dark truncate'>{user.username}</p>
                            <p className='text-caption-2-regular text-gray truncate'>{user.email}</p>
                        </div>
                    </div>
                )}

                {/* Other Accounts */}
                {otherAccounts.map(([key, account]) => (
                    <button
                        key={key}
                        className='bg-canvas shadow-nucleus-elevation-medium flex w-full cursor-pointer items-center gap-4 rounded-lg p-3 text-left'
                        onClick={() => handleSwitchAccount(key)}
                    >
                        <img
                            src={getProfileImage(account.image)}
                            alt={account.username || 'User'}
                            className='size-9 rounded-full border-2 border-transparent object-cover'
                        />
                        <div className='min-w-0 flex-1'>
                            <p className='text-label-sm-bold text-dark truncate'>{account.username}</p>
                            <p className='text-caption-2-regular text-gray truncate'>{account.email}</p>
                        </div>
                    </button>
                ))}

                {/* Add Account */}
                <button
                    className='shadow-nucleus-elevation-medium flex w-full cursor-pointer items-center gap-4 rounded-lg bg-white p-3 text-left'
                    onClick={handleAddAccount}
                >
                    <div className='bg-brand-subtle shadow-nucleus-elevation-low flex size-9 items-center justify-center rounded-lg'>
                        <IcIntegrations className='text-dark size-5' />
                    </div>
                    <span className='text-label-sm-bold text-dark'>Add Account</span>
                </button>
                <button
                    className='shadow-nucleus-elevation-medium flex w-full cursor-pointer items-center gap-4 rounded-lg bg-white p-3 text-left'
                    onClick={() => push('/support')}
                >
                    <div className='bg-brand-subtle shadow-nucleus-elevation-low flex size-9 items-center justify-center rounded-lg'>
                        <IcHelpCircle className='text-dark size-5' />
                    </div>
                    <span className='text-label-sm-bold text-dark'>Get Help</span>
                </button>

                {/* Logout */}
                <button
                    className='shadow-nucleus-elevation-medium flex w-full cursor-pointer items-center gap-4 rounded-lg bg-white p-3 text-left'
                    onClick={handleLogout}
                >
                    <div className='bg-brand-subtle shadow-nucleus-elevation-low flex size-9 items-center justify-center rounded-lg'>
                        <IcLogout className='text-dark size-5' />
                    </div>
                    <span className='text-label-sm-bold text-dark'>{logoutText}</span>
                </button>
            </div>
        </div>
    );
};
